/**
 * Generates the copyable AI prompt text for poll JSON upload.
 *
 * The prompt instructs an external AI to output valid JSON matching
 * the canonical poll upload schema.
 */

import {
  SCHEMA_VERSION,
  POLL_KINDS,
  VOTE_TYPES,
  VOTE_VISIBILITY_VALUES,
  VOTE_ANONYMIZATION_VALUES,
} from "./constants.js";

// --- Shared fragments ---

function enumBlock() {
  return `--- Enum values ---
voteVisibility: ${VOTE_VISIBILITY_VALUES.join(", ")}
voteAnonymization: ${VOTE_ANONYMIZATION_VALUES.join(", ")}
voteType: ${VOTE_TYPES.join(", ")}`;
}

function basicFieldsDocs() {
  return `- title (string, REQUIRED)
- description (string, optional, supports markdown)
- voteVisibility (enum, optional, default "full_visibility")
- voteAnonymization (enum, optional, default "none")
- hideVoterIdentities (boolean, optional, default false)
- settings (object, optional):
  - voteType: ${VOTE_TYPES.join(" or ")} (default "MULTIPLE_CHOICE")
  - allowMultiple (boolean, default false)
  - maxSelections (integer or null, default null; when set, must be 2 to the option count, plus 1 if write-in is enabled)
  - allowWriteIn (boolean, default false)
  - deadlineAt (ISO 8601 UTC string or null, default null)
- options (array, REQUIRED, min 2 items): each { "label": "text", "note": "optional markdown" }`;
}

function privacyConstraint() {
  return "- When voteVisibility is \"full_visibility\", hideVoterIdentities must be false.";
}

// --- Session prompt (was generatePollSchemaPrompt) ---

export function generateSessionPollSchemaPrompt() {
  return `You are generating a JSON payload for Quest Scheduler, a tabletop session scheduling app.

OUTPUT RULES:
- Return ONLY valid JSON. No markdown fences, no commentary, no explanation.
- The response must be a single JSON object.

SCHEMA (version ${SCHEMA_VERSION}):

Top-level envelope:
{
  "schemaVersion": "${SCHEMA_VERSION}",
  "pollKind": "SESSION",
  "payload": { ... }
}

--- SESSION payload fields ---
- title (string, optional, default "Untitled poll")
- description (string, optional, supports markdown)
- timezone (string, IANA timezone, e.g. "America/New_York")
- questingGroup (object, optional): { "name": "Group Name" }
- invitees (string[], optional): email addresses or Discord usernames
- allowLinkSharing (boolean, optional, default false)
- voteVisibility (enum, optional, default "full_visibility")
- voteAnonymization (enum, optional, default "none")
- hideVoterIdentities (boolean, optional, default false)
- slots (array, REQUIRED, min 1 item): each { "startUtcIso": "ISO8601 UTC", "endUtcIso": "ISO8601 UTC" }
- embeddedPolls (array, optional): array of add-on poll objects (see below) with "required" (boolean)

--- Embedded poll fields (used in embeddedPolls array) ---
${basicFieldsDocs()}
- required (boolean, optional, default false)

${enumBlock()}

--- Constraints ---
- All timestamps must be ISO 8601 in UTC (ending with Z).
- Slot endUtcIso must be after startUtcIso.
${privacyConstraint()}
- SESSION polls require at least 1 slot.
- Each embedded poll requires at least 2 options with non-empty labels.
- The description and option note fields support GitHub Flavored Markdown (bold, italic, links, lists, tables). Use markdown formatting when it improves readability.

--- EXAMPLE: Session Poll ---
{
  "schemaVersion": "1.0",
  "pollKind": "SESSION",
  "payload": {
    "title": "Campaign 12 Session Scheduling",
    "description": "Finding a time for the next Curse of Strahd session.",
    "timezone": "America/New_York",
    "questingGroup": { "name": "Curse of Strahd Gang" },
    "invitees": ["player1@example.com", "player2@example.com"],
    "allowLinkSharing": false,
    "voteVisibility": "hidden_while_voting",
    "voteAnonymization": "none",
    "hideVoterIdentities": false,
    "slots": [
      { "startUtcIso": "2026-03-15T18:00:00Z", "endUtcIso": "2026-03-15T22:00:00Z" },
      { "startUtcIso": "2026-03-16T14:00:00Z", "endUtcIso": "2026-03-16T18:00:00Z" }
    ],
    "embeddedPolls": [
      {
        "title": "Snack rotation",
        "description": "Whose turn to bring snacks?",
        "required": true,
        "settings": { "voteType": "MULTIPLE_CHOICE", "allowMultiple": false },
        "options": [
          { "label": "Alice" },
          { "label": "Bob" },
          { "label": "Charlie" }
        ]
      }
    ]
  }
}

Now generate a SESSION poll JSON based on the user's request. Always use pollKind "SESSION" with time slots.`;
}

// --- Basic prompt ---

export function generateBasicPollSchemaPrompt() {
  return `You are generating a JSON payload for Quest Scheduler, a tabletop session scheduling app.

OUTPUT RULES:
- Return ONLY valid JSON. No markdown fences, no commentary, no explanation.
- The response must be a single JSON object.

SCHEMA (version ${SCHEMA_VERSION}):

Top-level envelope:
{
  "schemaVersion": "${SCHEMA_VERSION}",
  "pollKind": "BASIC",
  "payload": { ... }
}

--- BASIC payload fields ---
${basicFieldsDocs()}
- questingGroup (object, optional): { "name": "Group Name" }

${enumBlock()}

--- Constraints ---
${privacyConstraint()}
- BASIC polls require at least 2 options with non-empty labels.
- The description and option note fields support GitHub Flavored Markdown (bold, italic, links, lists, tables). Use markdown formatting when it improves readability.

--- EXAMPLE: Standalone Basic Poll ---
{
  "schemaVersion": "1.0",
  "pollKind": "BASIC",
  "payload": {
    "title": "What adventure module next?",
    "description": "Vote for the next campaign after we finish Curse of Strahd.",
    "questingGroup": { "name": "Curse of Strahd Gang" },
    "voteVisibility": "hidden_until_all_voted",
    "voteAnonymization": "none",
    "hideVoterIdentities": false,
    "settings": {
      "voteType": "RANKED_CHOICE",
      "allowMultiple": false,
      "maxSelections": null,
      "allowWriteIn": false,
      "deadlineAt": null
    },
    "options": [
      { "label": "Tomb of Annihilation" },
      { "label": "Storm King's Thunder" },
      { "label": "Waterdeep: Dragon Heist", "note": "Shorter campaign, ~level 1-5" },
      { "label": "Descent into Avernus" }
    ]
  }
}

Now generate a BASIC poll JSON based on the user's request. Always use pollKind "BASIC" with voting options (not time slots).`;
}

// --- Add-on prompt ---

export function generateAddonPollSchemaPrompt() {
  return `You are generating add-on poll JSON for Quest Scheduler, a tabletop session scheduling app.

OUTPUT RULES:
- Return ONLY valid JSON. No markdown fences, no commentary, no explanation.
- The response must be a SINGLE poll object OR an array of poll objects.
- Do NOT wrap in a schemaVersion/pollKind envelope. Return the raw poll object(s) directly.

--- Add-on poll fields ---
- title (string, REQUIRED)
- description (string, optional, supports markdown)
- required (boolean, optional, default false) — whether participants must complete this poll
- voteVisibility (enum, optional, default "full_visibility")
- voteAnonymization (enum, optional, default "none")
- hideVoterIdentities (boolean, optional, default false)
- settings (object, optional):
  - voteType: ${VOTE_TYPES.join(" or ")} (default "MULTIPLE_CHOICE")
  - allowMultiple (boolean, default false)
  - maxSelections (integer or null, default null; when set, must be 2 to the option count, plus 1 if write-in is enabled)
  - allowWriteIn (boolean, default false)
  - deadlineAt (ISO 8601 UTC string or null, default null)
- options (array, REQUIRED, min 2 items): each { "label": "text", "note": "optional markdown" }

${enumBlock()}

--- Constraints ---
${privacyConstraint()}
- Each poll requires at least 2 options with non-empty labels.
- The description and option note fields support GitHub Flavored Markdown (bold, italic, links, lists, tables). Use markdown formatting when it improves readability.

--- EXAMPLE: Single add-on poll ---
{
  "title": "Snack rotation",
  "description": "Whose turn to bring snacks?",
  "required": true,
  "settings": { "voteType": "MULTIPLE_CHOICE", "allowMultiple": false },
  "options": [
    { "label": "Alice" },
    { "label": "Bob" },
    { "label": "Charlie" }
  ]
}

--- EXAMPLE: Multiple add-on polls ---
[
  {
    "title": "Session length preference",
    "required": false,
    "options": [
      { "label": "3 hours" },
      { "label": "4 hours" },
      { "label": "5+ hours" }
    ]
  },
  {
    "title": "Snack duty",
    "required": true,
    "options": [
      { "label": "Alice" },
      { "label": "Bob" }
    ]
  }
]

Now generate add-on poll JSON based on the user's request. These polls are attached to an existing session — do NOT include schemaVersion, pollKind, slots, or invitees.`;
}

/** @deprecated Use generateSessionPollSchemaPrompt instead. */
export const generatePollSchemaPrompt = generateSessionPollSchemaPrompt;
